import React, { useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, AlertCircle, Shield, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import InterventionForm from '../components/InterventionForm';
import { track, whatsappUrl } from '../lib/track';
import { mockData } from '../mock';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Contact & demande d'intervention | Frelon33™ Gironde";
  }, []);

  const handleCall = () => {
    track('phone_click', { location: 'contact' });
    window.location.href = 'tel:0676583038';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-green-50/30">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-yellow-500 text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center space-x-2 font-medium mb-4 text-white/90">
            <AlertCircle className="h-5 w-5" />
            <span>Intervention rapide en Gironde</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4">
            Demande d'intervention
          </h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed opacity-95">
            Décrivez votre problème de nuisibles et notre équipe
            <strong> vous recontacte rapidement</strong> avec un devis gratuit.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-8">

            {/* Emergency Call */}
            <Card className="border-l-4 border-green-600 bg-gradient-to-br from-green-50 to-yellow-50 hover-lift">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-green-800">
                  <Phone className="h-5 w-5" />
                  <span>Urgence 7j/7</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-green-700 mb-4 leading-relaxed">
                  En cas de danger immédiat (frelon asiatique, nid accessible, essaim agressif).
                </p>
                <Button
                  onClick={handleCall}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 text-lg transition-all duration-200"
                  data-testid="contact-emergency-call-button"
                  data-gtm-event="phone_click"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  06 76 58 30 38
                </Button>
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('whatsapp_click', { location: 'contact' })}
                  className="mt-3 w-full rounded-md text-white font-semibold py-3 text-base flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.02]"
                  style={{ backgroundColor: '#25D366' }}
                  data-testid="contact-whatsapp-button"
                  data-gtm-event="whatsapp_click"
                >
                  <MessageCircle className="h-5 w-5" />
                  Écrire sur WhatsApp
                </a>
              </CardContent>
            </Card>

            {/* Contact Details */}
            <Card className="hover-lift">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-green-600" />
                  <span>Nos coordonnées</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                  <Phone className="h-5 w-5 text-green-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Téléphone</p>
                    <a href="tel:0676583038" onClick={handleCall} className="text-gray-600 hover:text-green-600 transition-colors">
                      06 76 58 30 38
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                  <Mail className="h-5 w-5 text-green-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <a href="mailto:frelon33000@gmail.com" className="text-gray-600 hover:text-green-600 transition-colors break-all">
                      frelon33000@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-green-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Zone d'intervention</p>
                    <p className="text-gray-600">Cadaujac, Bordeaux<br />et toute la Gironde</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-green-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Disponibilité</p>
                    <p className="text-gray-600">7j/7 - Intervention sous 48h</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Areas */}
            <Card className="hover-lift">
              <CardHeader>
                <CardTitle>Principales communes desservies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  {mockData.serviceAreas.slice(0, 10).map((area, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      <span>{area}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-4 font-medium">
                  Et toutes les autres communes de Gironde.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                <Shield className="h-6 w-6 text-green-600" />
                Formulaire de demande d'intervention
              </h2>
              <p className="text-gray-600 mt-2">
                Remplissez ce formulaire, <strong>Frelon33™</strong> vous recontacte rapidement avec un devis gratuit.
              </p>
            </div>

            {/* Safety warning */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-800 leading-relaxed">
                  <strong>Important :</strong> n'essayez jamais de détruire un nid vous-même.
                  Gardez vos distances et faites appel à un professionnel.
                </p>
              </div>
            </div>

            <InterventionForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
